import { useEffect, useRef } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';

function ExperienceLayer() {
  const canvasRef = useRef(null);
  const glowRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 130,
    damping: 24,
    restDelta: 0.001
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const glow = glowRef.current;
    const context = canvas.getContext('2d');
    const particles = [];
    const pointer = { x: -100, y: -100, lastX: -100, lastY: -100 };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarsePointer = window.matchMedia('(pointer: coarse)');
    let frameId;

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * ratio;
      canvas.height = window.innerHeight * ratio;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const createParticle = (x, y, intensity) => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.25 + Math.random() * intensity;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 0.2,
        life: 1,
        decay: 0.018 + Math.random() * 0.025,
        size: 1 + Math.random() * 3.5,
        twinkle: Math.random() * Math.PI
      });
    };

    const move = ({ clientX, clientY }) => {
      const distance = Math.hypot(clientX - pointer.lastX, clientY - pointer.lastY);
      pointer.x = clientX;
      pointer.y = clientY;
      glow.style.transform = `translate3d(${clientX}px, ${clientY}px, 0)`;

      if (distance > 2 && !reducedMotion.matches) {
        const count = Math.min(7, Math.ceil(distance / 6));
        for (let index = 0; index < count; index += 1) {
          const progress = index / count;
          createParticle(
            pointer.lastX + (clientX - pointer.lastX) * progress,
            pointer.lastY + (clientY - pointer.lastY) * progress,
            Math.min(distance / 15, 2.2)
          );
        }
      }

      pointer.lastX = clientX;
      pointer.lastY = clientY;
    };

    const render = () => {
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);
      context.globalCompositeOperation = 'lighter';

      for (let index = particles.length - 1; index >= 0; index -= 1) {
        const particle = particles[index];
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vx *= 0.97;
        particle.vy *= 0.97;
        particle.life -= particle.decay;
        particle.twinkle += 0.16;

        if (particle.life <= 0) {
          particles.splice(index, 1);
          continue;
        }

        const radius = particle.size * particle.life;
        const alpha = particle.life * (0.65 + Math.sin(particle.twinkle) * 0.25);
        const gradient = context.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          radius * 4
        );
        gradient.addColorStop(0, `rgba(255, 255, 220, ${alpha})`);
        gradient.addColorStop(0.25, `rgba(223, 255, 66, ${alpha * 0.8})`);
        gradient.addColorStop(1, 'rgba(223, 255, 66, 0)');
        context.fillStyle = gradient;
        context.beginPath();
        context.arc(particle.x, particle.y, radius * 4, 0, Math.PI * 2);
        context.fill();
      }

      frameId = requestAnimationFrame(render);
    };

    resize();
    render();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', move, { passive: true });

    if (coarsePointer.matches) {
      canvas.style.display = 'none';
      glow.style.display = 'none';
    }

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', move);
    };
  }, []);

  return (
    <>
      <motion.div className="scroll-progress" style={{ scaleX: progress }} />
      <canvas ref={canvasRef} className="cursor-trail" aria-hidden="true" />
      <div ref={glowRef} className="cursor-spark" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <div className="film-grain" aria-hidden="true" />
    </>
  );
}

export default ExperienceLayer;